import {
    Tooltip,
    IconButton,
    Grid,
    Divider,
    Box,
    Skeleton,
    Button,
    FormControlLabel,
    useMediaQuery, useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchMyDepragApi } from "../../../../../Infrastructure/Api/ApiBaseCall";
import { LanguageConsumer, getTranslation } from "../../../../../Infrastructure/Internationalisation/TranslationService"
import { useAppDispatch, useAppSelector } from "../../../../../Infrastructure/Store/hooks";
import { store, RootState } from "../../../../../Infrastructure/Store/store";
import { GRID_DEFAULT_LOCALE_TEXT_DE } from "../../../../../assets/DataGridLocalisation/DataGridLocale_de";
import { GRID_DEFAULT_LOCALE_TEXT_EN } from "../../../../../assets/DataGridLocalisation/DataGridLocale_en";
import { GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import { ArrowForward } from "@mui/icons-material";
import { StripedDataGrid } from "../../../../../Layout/DataGrid/StripedDataGrid";
import { DataGridSearch } from "../../../../../Layout/DataGrid/DataGridSearch";
import { MyGoldLicenceResultDto } from "../Model/MyGoldLicenceResultDto";
import { MyGoldLicenceItem } from "../Model/MyGoldLicenceItem";
import { setSelectedLicenceIds } from "../Assign/Store/MyGoldAssignSlice";
import { CopyChip } from "../../../../../Layout/CopyChip";
import { CustomSwitch } from "../../../../../Layout/Switch/CustomSwitch";

export const MyGoldTable = () => {
    const dispatch = useAppDispatch();
    const language = useAppSelector(store => store.settings.language);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));

    const [searchValue, setSearchValue] = useState<string>("");
    const [showLicensed, setShowLicensed] = useState(false);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridSelectionModel>([]);
    
    const { isLoading, data, refetch } = useQuery({
        queryKey: ["myGoldLicence"],
        queryFn: async () => await fetchMyDepragApi<MyGoldLicenceResultDto>(
             {PreferredIsoLanguageCode: language},
            "licences/mygold",
            'POST',
            dispatch,
            store.getState() as RootState)
    });

    useEffect(() => {
        refetch();
    }, [location, refetch]);


    const search = (inputText: string) => {
        setSearchValue(inputText);
    }

    const convertToDateString = (date: Date | string) => {
        return new Date(date).toLocaleDateString(language, {month: '2-digit', day: '2-digit', year: 'numeric'})
    }

    const showAssignedSwitch = data && data.HasAssignedLicences
    const rowsSelected = rowSelectionModel.length > 0

    const columns: GridColDef[] = [
        {
            field: 'SapSalesOrder',
            headerName: getTranslation("MyGoldLicenceSapSalesOrder"),
            flex: 1,
            minWidth: 150,
            renderCell: (params: GridRenderCellParams<any, MyGoldLicenceItem, any>) => {
                return <CopyChip showIconAfterCopy label={params.row.SapSalesOrder} isCopyEnabled  />
            }
        },
        {
            field: 'PurchaseOrderNumber',
            headerName: getTranslation("MyGoldLicencePurchaseOrderNumber"),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'PurchaseOrderDate',
            headerName: getTranslation("MyGoldLicencePurchaseOrderDate"),
            flex: 1,
            minWidth: 150,
            valueGetter: (params: GridRenderCellParams<any, MyGoldLicenceItem, any>) => convertToDateString(params.row.PurchaseOrderDate),
        },
        {
            field: 'ProductName',
            headerName: getTranslation("MyGoldLicenceProductName"),
            flex: 1,
            minWidth: 150,
        },
        {
            field: '',
            headerName: '',
            flex: 0.1,
            minWidth: 100,
            align: 'right',
            renderCell: (params: GridRenderCellParams<any, MyGoldLicenceItem, any>) => (
                <strong>
                    <Tooltip title={getTranslation("AssignLicence")}>
                        <IconButton color={'darky'} onClick={() => {
                            dispatch(setSelectedLicenceIds({
                                myGoldLicenseIds: [params.row.MyGoldLicenceId],
                                assignedToCustomerId: params.row.AssignedToCustomerId,
                                resetManyAssigned: false
                            }));
                            navigate("/licences/MyGold/assign");
                            }}>
                            <ArrowForward />
                        </IconButton>
                    </Tooltip>
                </strong>
            ),
            sortable: false,
            filterable: false
        },
    ];

    const rows = data
      ? data.Items.filter(x =>
        x.IsAssigned === showLicensed &&
        (x.MyGoldLicenceId?.toString().includes(searchValue.toLocaleLowerCase()) ||
          (x.ProductName?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
          (convertToDateString(x.PurchaseOrderDate).toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
          (x.PurchaseOrderNumber?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
          (x.SapSalesOrder?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()))), )
      : [];

    return (
        <LanguageConsumer>
            {({ getTranslatedText }) =>
                <>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12} textAlign='center'>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sx={{ textAlign: 'left' }}>

                                    <div
                                        dangerouslySetInnerHTML={{ __html: getTranslatedText("MyGoldLicencesInfoText") }}
                                    />

                                    <Divider sx={{ my: 3, mb: 2 }} />
                                </Grid>

                                <Grid item xs={12} sm={6} my={'auto'} sx={{textAlign: 'left', overflowX: 'hidden'}}>
                                  <DataGridSearch searchValue={searchValue} search={search} />
                                </Grid>

                                {showAssignedSwitch &&
                                  <Grid item xs={12} sm={6} md={rowsSelected ? 3 : 6} my={"auto"}
                                        sx={{textAlign: {xs: "left", sm: "right"}, ml: {xs:2, sm:0}}}>
                                      <FormControlLabel control={<CustomSwitch/>} checked={showLicensed}
                                                        onChange={(e, value) => setShowLicensed(value)}
                                                        label={getTranslatedText("ShowAssigned")}
                                                        sx={{textAlign: "left"}}/>
                                  </Grid>
                                }

                                {rowsSelected && <Grid item xs={12} sm={6} md={showAssignedSwitch ? 3 : 6}
                                                       my={"auto"}
                                                       sx={{
                                                           textAlign: {
                                                               xs: "left",
                                                               sm: showAssignedSwitch ? "left" : "right",
                                                               md: "right",
                                                           },
                                                       }}>
                                    <Button startIcon={<ArrowForward/>} sx={{ml: 1}} variant="contained" color="success"
                                            onClick={() => {
                                                dispatch(setSelectedLicenceIds({myGoldLicenseIds: rowSelectionModel as number[], resetManyAssigned: showLicensed ? true : false}));
                                        navigate("/licences/MyGold/assign");
                                    }}>{getTranslatedText("AssignAmountLicence", [rowSelectionModel.length.toString()])}</Button>
                                </Grid>}
                            
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <Box component={'div'} sx={{ width: '100%' }}>
                                    <StripedDataGrid
                                        localeText={language === "de" ? GRID_DEFAULT_LOCALE_TEXT_DE : GRID_DEFAULT_LOCALE_TEXT_EN}
                                        rows={rows}
                                        autoHeight={true}
                                        columns={columns}
                                        pageSize={8}
                                        rowsPerPageOptions={[8]}
                                        checkboxSelection={true}
                                        disableColumnFilter={false}
                                        disableColumnMenu={false}
                                        disableSelectionOnClick={true}
                                        disableVirtualization={false}
                                        hideFooter={false}
                                        selectionModel={rowSelectionModel}
                                        onSelectionModelChange={setRowSelectionModel}
                                        getRowId={(x) => x.MyGoldLicenceId}
                                        getRowClassName={(params) =>
                                            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                          }
                                        components={{
                                            LoadingOverlay: () => <Skeleton variant="rectangular" height={100} sx={{ my: 4, mx: 1, mt: 1 }} />,
                                        }}
                                        loading={isLoading}
                                    />
                                </Box>
                            </Grid>
                                    

                        </Grid>
                    </Grid>
                </>
            }
        </LanguageConsumer>
    )   
}